import { useState, useEffect, useRef } from "react";
import InlineSelect from "./GridSearchView/InlineSelect";
import SearchInput from "./GridSearchView/SearchInput";
import CustomGridDrawing from "./GridSearchView/CustomGridDrawing";
import { fetchDistricts, fetchStreets, searchScenes } from "@/services/dataserver";

interface GridSearchProps {
  city: string;
  drawingMode: boolean;
  onDrawingChange: (drawing: boolean) => void;
  onLocate?: (coords: [number, number], zoom?: number) => void;
  onGridSelect?: (info: any) => void;
  onDataFromGridSearch?: (data: any) => void;
  aiSearchQuery?: string;
}

export default function GridSearch({ city, drawingMode, onDrawingChange, onLocate, onGridSelect, onDataFromGridSearch, aiSearchQuery = "" }: GridSearchProps) {
  const [keyword, setKeyword] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [hasSearched, setHasSearched] = useState(false); // 标记是否已执行搜索
  const [district, setDistrict] = useState("");
  const [districtOptions, setDistrictOptions] = useState<any[]>([]);
  const [street, setStreet] = useState("");
  const [streetsOptions, setStreetsOptions] = useState<any[]>([]);
  const [community, setCommunity] = useState("");
  const [grid, setGrid] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 模拟选项数据
  const districts = ["玄武区", "秦淮区", "建邺区", "鼓楼区"];
  const streets = ["街道A", "街道B", "街道C"];
  const communities = ["社区A", "社区B", "社区C"];
  const grids = ["网格001", "网格002", "网格003"];

  // 获取行政区划数据
  useEffect(() => {
    fetchDistricts().then(setDistrictOptions).catch(console.error);
  }, []);

  // 当区县选择改变时，返回所选区县的所有数据
  const handleDistrictChange = (data: any) => {
    if (!data) {
      // 清空区县 → 同时清空街道
      setDistrict("");
      setStreet("");
      setStreetsOptions([]);
      if (onDataFromGridSearch) {
        onDataFromGridSearch({ type: 'district', data: null });
      }
      return;
    }
    setDistrict(data.name);
    // 清空街道
    setStreet("");
    // 传递区县数据给父组件
    if (onDataFromGridSearch) {
      onDataFromGridSearch({type:'district', data: data });
    }
    fetchStreets(data.name, 3, data.gid).then(setStreetsOptions).catch(console.error);
  };
  const handleStreetChange = (data: any) => {
    if (!data) {
      setStreet("");
      if (onDataFromGridSearch) {
        onDataFromGridSearch({ type: 'street', data: null });
      }
      return;
    }
    setStreet(data.name);
    // 传递街道数据给父组件
    if (onDataFromGridSearch) {
      onDataFromGridSearch({type:'street', data: data });
    }
  }


  // 关闭下拉框
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // 搜索防抖
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // AI 自动选择标记 + 延迟定时器
  const aiPendingRef = useRef(false);
  const aiSelectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      if (aiSelectTimerRef.current) clearTimeout(aiSelectTimerRef.current);
    };
  }, []);

  // AI 搜索：接收 FastGPT 返回的查询词并触发搜索
  useEffect(() => {
    if (aiSearchQuery) {
      aiPendingRef.current = true;
      handleSearch(aiSearchQuery);
    }
  }, [aiSearchQuery]);

  const handleSearch = (val: string) => {
    setKeyword(val);
    // 用户手动输入时取消 AI 自动选择
    if (aiSelectTimerRef.current) {
      clearTimeout(aiSelectTimerRef.current);
      aiSelectTimerRef.current = null;
      aiPendingRef.current = false;
    }
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = null;
    }
    if (!val.trim()) {
      setSearchResults([]);
      setShowDropdown(false);
      setHasSearched(false);
      return;
    }
    // 300ms 防抖
    searchTimerRef.current = setTimeout(async () => {
      try {
        const list = await searchScenes(val.trim());
        setHasSearched(true);
        if (!Array.isArray(list)) {
          setSearchResults([]);
          setShowDropdown(true);
          return;
        }
        // 映射为 SearchInput 需要的格式
        const mapped = list.map((item: any) => ({
          name: item.name || item.NAME || '未知',
          type: item.type || item.TYPE || '场景',
          gid: item.gid || item.GID || item.id || item.ID,
          objectid: item.objectid || item.OBJECTID || '',
          extractType: item['提取'] || item.extractType || item.type || '',
          center: [parseFloat(item.lon || item.LON || 0), parseFloat(item.lat || item.LAT || 0)] as [number, number],
          path: item.path || item.addr || item.ADDR || '',
        }));
        setSearchResults(mapped);
        setShowDropdown(true);
        // AI 搜索延迟 1 秒后自动选择，让用户先看到列表
        if (aiPendingRef.current && mapped.length > 0) {
          aiSelectTimerRef.current = setTimeout(() => {
            handleSelectResult(mapped[0]);
            aiPendingRef.current = false;
            aiSelectTimerRef.current = null;
          }, 1000);
        }
      } catch (err) {
        console.error('场景搜索失败:', err);
        setSearchResults([]);
        setHasSearched(true);
        setShowDropdown(true);
      }
    }, 300);
  };

    const handleSelectResult = (result: any) => {
      // 选中结果 → 取消待发送的搜索请求
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
        searchTimerRef.current = null;
      }
      setKeyword(result.name);
      setSearchResults([]);
      setShowDropdown(false);
      // 通知父组件绘制面（AI触发时带objectid加载POI，手动操作仅绘制场景面）
      if (onDataFromGridSearch && (result.gid || result.id)) {
        const data: any = { gid: result.gid || result.id };
        // AI 自动选择时才传递 objectid + extractType，触发 POI 列表和画像弹窗
        if (aiPendingRef.current && result.objectid) {
          data.objectid = result.objectid;
          data.extractType = result.extractType || '';
          data.name = result.name || '';
        }
        onDataFromGridSearch({ type: 'scene', data });
      }
      if (result.center && result.center[0] && result.center[1]) {
        onLocate(result.center);
      }
    };

  return (
    <div className="absolute top-20 right-4 z-30 w-60">
      <div
        className="bg-white/92 backdrop-blur-md border border-slate-200/80 rounded-xl overflow-visible"
        style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.10)" }}
      >
        <div className="p-3">
          <div className="flex items-center gap-1.5 mb-2.5">
            <span className="w-0.5 h-4 bg-slate-700 rounded-full flex-shrink-0" />
            <span className="text-slate-800 text-sm font-semibold">网格搜索</span>
          </div>

          {/* 输入框搜索 */}
          <div className="relative mb-3">
            <SearchInput
              keyword={keyword}
              onChange={handleSearch}
              placeholder="搜索小区、街道、社区..."
              searchResults={searchResults}
              showDropdown={showDropdown}
              onResultClick={handleSelectResult}
              onCloseDropdown={() => setShowDropdown(false)}
              results={searchResults}
            />
          </div>

          {/* 省市显示 */}
          <div className="flex items-center gap-1.5 mb-2">
            <div className="flex-1 flex items-center gap-1 bg-slate-50 border border-slate-200 rounded px-2 py-1 text-xs text-slate-600 whitespace-nowrap">
              <i className="ri-map-2-line text-slate-400 text-xs flex-shrink-0" />
              <span className="truncate">江苏</span>
            </div>
            <div className="flex-1 flex items-center gap-1 bg-slate-50 border border-slate-200 rounded px-2 py-1 text-xs text-slate-600 whitespace-nowrap">
              <i className="ri-building-2-line text-slate-400 text-xs flex-shrink-0" />
              <span className="truncate">{city}</span>
            </div>
          </div>

          {/* 城市选择 */}
          <div className="grid grid-cols-2 gap-1.5 mb-3">
            <InlineSelect value={district} placeholder="区县" options={districtOptions} onChange={handleDistrictChange} />
            <InlineSelect value={street} placeholder="街道" options={streetsOptions} onChange={handleStreetChange} disabled={!district} />
            {/* <InlineSelect value={community} placeholder="社区" options={communities} onChange={setCommunity} disabled={!street} /> */}
            {/* <InlineSelect value={grid} placeholder="网格" options={grids} onChange={setGrid} disabled={!community} /> */}
          </div>

          {/* Grid overview hint when selection active */}
          {(street || community || grid) && (
            <div className="mb-2.5 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <i className="ri-grid-line text-slate-500 text-xs"></i>
              </div>
              <span className="text-xs text-slate-600 flex-1">
                已选：<span className="font-medium text-slate-800">{grid || community || street}</span>
              </span>
              {/* <span className="text-xs text-slate-400">概览已显示</span> */}
            </div>
          )}

          {/* Custom grid drawing */}
          <CustomGridDrawing drawingMode={drawingMode} onDrawingChange={onDrawingChange} />
        </div>
      </div>
    </div>
  );
}